import { t } from "@lingui/macro";

import {
  formatDateToUserDate,
  formatToLocaleForm,
  formatToUserTimeZone
} from "../../libs/utils/date.js";
import {
  emailRegEx,
  urlRegEx,
  alphabetRegEx,
  noSpecialCharactersRegEx,
  alphanumericRegEx,
  numberRegEx
} from "./validation.regex.js";

const formatChecklist = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  value = value.filter((item) => {
    return item.selected;
  });
  value = value.map((item) => {
    return item.value;
  });
  return value;
};

export const isBlank = (value) =>
  [null, "", undefined].includes(value) ||
  (Array.isArray(value) && value.isEmpty());

const isEmpty = (value) => (isBlank(value) ? undefined : "is not empty");

const isNotEmpty = (value) => (!isBlank(value) ? undefined : "is empty");

const getDisplayValue = (value, fieldName, type) => {
  let displayValue = fieldName || value;
  if (!fieldName && value && (type === "Date" || type === "DateTime")) {
    let includeTime = type === "DateTime";
    let dateString = formatDateToUserDate(value, includeTime);
    displayValue = includeTime
      ? formatToUserTimeZone(dateString)
      : formatToLocaleForm(dateString, includeTime);
  } else if (
    !fieldName &&
    value &&
    (type === "User" || type === "UserAndGroup")
  ) {
    displayValue = value.Name;
  }
  return displayValue;
};

export const required = (value, type) => {
  let isRequired = false;
  if (type === "Checklist") {
    if (Array.isArray(value)) {
      let data = value.filter((item) => {
        return item.selected;
      });
      isRequired = data.length >= 1 ? true : undefined;
    } else {
      isRequired = true;
    }
  } else if (Array.isArray(value)) {
    isRequired = value.length > 0;
  } else {
    isRequired = value || value === 0;
  }
  return isRequired ? undefined : t`Value is required`;
};

const stringValueToLowerCase = (source, targetId) => {
  const rowValue = source.getValue(targetId);
  return typeof rowValue === "string" ? rowValue.toLowerCase() : rowValue;
};

const findDuplicatesWithCaseInsenitive = (rows, fieldId) => {
  const tempRows = new Set();
  const duplicateRows = new Set();
  for (const row of rows) {
    let field = stringValueToLowerCase(row, fieldId);
    if (field !== "") {
      if (!tempRows.has(field)) {
        tempRows.add(field);
      } else {
        duplicateRows.add(field);
      }
    }
  }
  return duplicateRows;
};

export const isUnique = ({ formData, field, visibleRows, errorMessage }) => {
  const fieldId = field.id;
  if (!isBlank(formData.getValue(fieldId))) {
    const isDuplicate = findDuplicatesWithCaseInsenitive(
      visibleRows,
      fieldId
    ).has(stringValueToLowerCase(formData, fieldId));
    if (isDuplicate) {
      const error = errorMessage;
      return error ? error : t`Each record must be unique`;
    }
    return undefined;
  }
};

export const maxLength = (value, max) =>
  value && value.length > parseInt(max, 10)
    ? t`Value must be ${max} characters or less`
    : undefined;

export const minLength = (value, min) =>
  value && value.length < parseInt(min, 10)
    ? t`Value must be ${min} characters or more`
    : undefined;

export const number = (value) =>
  value && isNaN(Number(value)) ? t`Value must be a number` : undefined;

//should match unicode character?
export const isAlphabet = (value) =>
  value && !alphabetRegEx.test(value)
    ? t`Value must contain only alphabets`
    : undefined;

export const noSpecialCharacters = (value) =>
  value && noSpecialCharactersRegEx.test(value)
    ? t`Value must not contain special character`
    : undefined;

export const isAlphanumeric = (value) =>
  isBlank(value) || (value && alphanumericRegEx.test(value))
    ? undefined
    : t`Value must be alphanumeric`;

export const isNumeric = (value) =>
  isBlank(value) || (value && numberRegEx.test(value))
    ? undefined
    : t`Value must be numeric`;

export const isEmail = (value) => {
  return isBlank(value) || (value && emailRegEx.test(value))
    ? undefined
    : t`Value must be a valid email`;
};

export const isUrl = (value) => {
  urlRegEx.lastIndex = 0;
  return isBlank(value) || (value && urlRegEx.test(value))
    ? undefined
    : t`Value must be a valid URL`;
};

export const contains = (container, value, fieldName, type) => {
  if (type === "Checklist" && Array.isArray(container)) {
    container = formatChecklist(container);
  }
  return !value || !container || container.includes(value)
    ? undefined
    : t`Value does not contain ${value}`;
};

export const notContains = (container, value, fieldName, type) => {
  if (type === "Checklist" && Array.isArray(container)) {
    container = formatChecklist(container);
  }
  return !value || !container || !container.includes(value)
    ? undefined
    : t`Value should not contain ${value}`;
};

export const isPartOf = (value, container, fieldName, type) => {
  if (type === "User") {
    container =
      container &&
      container.map((item) => {
        return item._id;
      });
    value = value && value._id;
  } else if (type === "MultiUser") {
    container =
      container &&
      container.map((item) => {
        return item._id;
      });
    value =
      value &&
      value.map((item) => {
        return item._id;
      });
    if (container && value && container.length >= value.length) {
      let isPartOf = true;
      for (let i = 0; i < value.length; i++) {
        if (!container.includes(value[i])) {
          isPartOf = false;
          break;
        }
      }
      return !isPartOf;
    }
    return true;
  }
  return isBlank(value) || (!isBlank(container) && container.includes(value))
    ? undefined
    : t`Value is not part of ${container}`;
};

export const isNotPartOf = (value, container, fieldName, type) => {
  if (type === "User") {
    container =
      container &&
      container.map((item) => {
        return item._id;
      });
    value = value && value._id;
  } else if (type === "MultiUser") {
    container =
      container &&
      container.map((item) => {
        return item._id;
      });
    value =
      value &&
      value.map((item) => {
        return item._id;
      });
    if (container && value && container.length >= value.length) {
      let isNotPartOf = true;
      for (let i = 0; i < value.length; i++) {
        if (container.includes(value[i])) {
          isNotPartOf = false;
        } else {
          isNotPartOf = true;
          break;
        }
      }
      return !isNotPartOf;
    }
    return undefined;
  }
  return isBlank(value) ||
    isBlank(container) ||
    (container && !container.includes(value))
    ? undefined
    : t`Value should not be a part of ${container}`;
};

export const lessThan = (LHS, RHS, fieldName, type) => {
  let displayValue = getDisplayValue(RHS, fieldName, type);
  return isBlank(LHS) || isBlank(RHS) || LHS < RHS
    ? undefined
    : t`Value must be less than ${displayValue}`;
};

export const lessThanEqualTo = (LHS, RHS, fieldName, type) => {
  let displayValue = getDisplayValue(RHS, fieldName, type);
  return isBlank(LHS) || isBlank(RHS) || LHS <= RHS
    ? undefined
    : t`Value must be less than or equal to ${displayValue}`;
};

export const greaterThan = (LHS, RHS, fieldName, type) => {
  let displayValue = getDisplayValue(RHS, fieldName, type);
  return isBlank(LHS) || isBlank(RHS) || LHS > RHS
    ? undefined
    : t`Value must be greater than ${displayValue}`;
};

export const greaterThanEqualTo = (LHS, RHS, fieldName, type) => {
  let displayValue = getDisplayValue(RHS, fieldName, type);
  return isBlank(LHS) || isBlank(RHS) || LHS >= RHS
    ? undefined
    : t`Value must be greater than or equal to ${displayValue}`;
};

export const equalTo = (LHS, RHS, fieldName, fieldType) => {
  if (fieldType === "Date" || fieldType === "DateTime") {
    let displayValue = getDisplayValue(RHS, fieldName, fieldType);
    return isBlank(LHS) ||
      isBlank(RHS) ||
      (LHS && RHS && LHS.getTime() === RHS.getTime())
      ? undefined
      : t`Value must be equal to ${displayValue}`;
  } else if (fieldType === "Boolean") {
    let value = "";
    let RHSValue = RHS;
    LHS = !!LHS;
    if (RHS && typeof RHS === "string") {
      RHSValue = RHSValue.toLowerCase();
    }
    if (["1", "true", "yes", 1, true].includes(RHSValue)) {
      value = true;
    } else {
      value = false;
    }
    return LHS === value
      ? undefined
      : t`Value must be equal to ${fieldName || RHS}`;
  } else if (fieldType === "User" || fieldType === "UserAndGroup") {
    if (isBlank(LHS) || (LHS && RHS && LHS._id === RHS._id)) {
      return undefined;
    } else {
      let displayValue = getDisplayValue(RHS, fieldName, fieldType);
      return `Value must be equal to ${displayValue}`;
    }
  } else if (fieldType === "MultiUser") {
    return undefined;
  } else {
    return isBlank(LHS) || LHS === RHS
      ? undefined
      : t`Value must be equal to ${fieldName || RHS}`;
  }
};
export const notEqualTo = (LHS, RHS, fieldName, fieldType) => {
  if (fieldType === "Date" || fieldType === "DateTime") {
    let displayValue = getDisplayValue(RHS, fieldName, fieldType);
    return isBlank(LHS) ||
      isBlank(RHS) ||
      (LHS && RHS && LHS.getTime() !== RHS.getTime())
      ? undefined
      : t`Value must not be equal to ${displayValue}`;
  } else if (fieldType === "Boolean") {
    let value = "";
    let RHSValue = RHS;
    LHS = !!LHS;
    if (RHS && typeof RHS === "string") {
      RHSValue = RHSValue.toLowerCase();
    }
    if (["1", "true", "yes", 1, true].includes(RHSValue)) {
      value = true;
    } else {
      value = false;
    }
    return LHS !== value
      ? undefined
      : t`Value must not be  ${fieldName || RHS}`;
  } else if (fieldType === "User" || fieldType === "UserAndGroup") {
    let displayValue = getDisplayValue(RHS, fieldName, fieldType);
    return isBlank(LHS) || (LHS && RHS && LHS._id !== RHS._id)
      ? undefined
      : t`Value must not be equal to ${displayValue}`;
  } else {
    return isBlank(LHS) || LHS !== RHS
      ? undefined
      : t`Value must not be equal to ${fieldName || RHS}`;
  }
};

export const isInTheFuture = (LHS, RHS, fieldName) =>
  isBlank(LHS) || LHS >= new Date()
    ? undefined
    : t`Value must be in the future`;

export const isNotInTheFuture = (LHS, RHS, fieldName) =>
  isBlank(LHS) || LHS <= new Date()
    ? undefined
    : t`Value must not be in the future`;

export const isInThePast = (LHS, RHS, fieldName, type) => {
  let RHSValue = new Date();
  if (type === "Date") {
    RHSValue = new Date(
      RHSValue.getFullYear(),
      RHSValue.getMonth(),
      RHSValue.getDate()
    );
    LHS = LHS && new Date(LHS.getFullYear(), LHS.getMonth(), LHS.getDate());
  }
  return isBlank(LHS) || LHS < RHSValue
    ? undefined
    : t`Value must be in the past`;
};

export const isNotInThePast = (LHS, RHS, fieldName, type) => {
  let RHSValue = new Date();
  if (type === "Date") {
    RHSValue = new Date(
      RHSValue.getFullYear(),
      RHSValue.getMonth(),
      RHSValue.getDate()
    );
    LHS = LHS && new Date(LHS.getFullYear(), LHS.getMonth(), LHS.getDate());
  } else {
    let seconds = RHSValue.getSeconds();
    let milliSeconds = RHSValue.getMilliseconds();
    LHS && LHS.setSeconds(seconds);
    LHS && LHS.setMilliseconds(milliSeconds);
  }
  return isBlank(LHS) || LHS >= RHSValue
    ? undefined
    : t`Value must not be in the past`;
};

export const isYes = (value) =>
  isBlank(value) || value ? undefined : t`Value must be Yes`;
const isNo = (value) =>
  isBlank(value) || !value ? undefined : t`Value must be No`;

const AND = (LHS, RHS) => !!LHS && !!RHS;
const OR = (LHS, RHS) => !!LHS || !!RHS;

export const maxSelection = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist" && Array.isArray(LHS)) {
    LHS = formatChecklist(LHS);
  }
  return !isBlank(LHS) && LHS.length > parseInt(RHS, 10)
    ? t`Number of selections should not be more than ${fieldName || RHS}`
    : undefined;
};

const minSelection = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist" && Array.isArray(LHS)) {
    LHS = formatChecklist(LHS);
  }
  return isBlank(LHS) || LHS.length >= parseInt(RHS, 10)
    ? undefined
    : t`Number of selection should be at least ${fieldName || RHS}`;
};

const selectAll = (LHS, RHS, fieldName, type) => {
  let filteredLHS = [];
  if (type === "Checklist" && Array.isArray(LHS)) {
    filteredLHS = formatChecklist(LHS);
  }
  return filteredLHS.isEmpty() ||
    (!filteredLHS.isEmpty() &&
      !LHS.isEmpty() &&
      filteredLHS.length === LHS.length)
    ? undefined
    : t`All items need to be selected`;
};

const selectionShouldContain = (LHS, RHS, fieldName) => {
  return isBlank(LHS) || (LHS && LHS.includes(RHS))
    ? undefined
    : t`Selection should contain ${fieldName || RHS}`;
};

const selectionShouldNotContain = (LHS, RHS, fieldName) => {
  return LHS && LHS.includes(RHS)
    ? t`Selection should not contain ${fieldName || RHS}`
    : undefined;
};

const attachmentShouldNotExceed = (LHS, RHS, fieldName) => {
  return undefined;
};

const attachmentType = (LHS, RHS, fieldName) => {
  return undefined;
};

const maxNumberOfAttachments = (LHS, RHS, fieldName) => {
  if (RHS && Number(RHS) && LHS && LHS.length > Number(RHS)) {
    return t`Should not exceed ${RHS} number of attachments`;
  }
  return undefined;
};

const minNumberOfAttachments = (LHS, RHS, fieldName) => {
  if (
    RHS &&
    Number(RHS) &&
    ((!isBlank(LHS) && LHS.length < Number(RHS)) || isBlank(LHS))
  ) {
    return t`Attach at least ${RHS} number of attachments`;
  }
  return undefined;
};

const countEqualTo = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS === RHS ? undefined : t`Selected count must be equal to ${RHS}`;
};
const countNotEqualTo = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS !== RHS
    ? undefined
    : t`Selected count must not be equal to ${RHS}`;
};
const countLessThan = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
  }
  if (Array.isArray(LHS)) {
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS > RHS ? undefined : t`Selected count must be less than ${RHS}`;
};
const countLessThanEqualTo = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
  }
  if (Array.isArray(LHS)) {
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS <= RHS
    ? undefined
    : t`Selected count must be less than or equal to ${RHS}`;
};
const countGreaterThan = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
  }
  if (Array.isArray(LHS)) {
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS > RHS ? undefined : t`Selected count must be greater than ${RHS}`;
};
const countGreaterThanEqualTo = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
  }
  if (Array.isArray(LHS)) {
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS >= RHS
    ? undefined
    : t`Selected count must be greater than or equal to ${RHS}`;
};

const domainEqualTo = (LHS, RHS, fieldName) => {
  if (!LHS) {
    return undefined;
  } else {
    let [emailName, emailDomain] = LHS.split("@");
    return emailName.length && emailDomain === RHS
      ? undefined
      : t`Domain should match with ${fieldName || RHS}`;
  }
};

const domainNotEqualTo = (LHS, RHS, fieldName) => {
  if (!LHS) {
    return undefined;
  } else {
    let [emailName, emailDomain] = LHS.split("@");
    return emailName.length && emailDomain !== RHS
      ? undefined
      : t`Domain should not match with ${fieldName || RHS}`;
  }
};

const startsWith = (LHS, RHS, fieldName) => {
  return !LHS || LHS.startsWith(RHS)
    ? undefined
    : t`should start with ${fieldName || RHS}`;
};

const endsWith = (LHS, RHS, fieldName) => {
  return !LHS || LHS.endsWith(RHS)
    ? undefined
    : t`should end with ${fieldName || RHS}`;
};

const customRegex = (LHS, RHS, FieldName, type) => {
  let regex = new RegExp(RHS);
  return !LHS || regex.test(LHS)
    ? ""
    : t`Entered value does not match the format ${RHS}`;
};

export const operatorMapping = {
  LESS_THAN: lessThan,
  LESS_THAN_OR_EQUAL_TO: lessThanEqualTo,
  EQUAL_TO: equalTo,
  NOT_EQUAL_TO: notEqualTo,
  GREATER_THAN: greaterThan,
  GREATER_THAN_OR_EQUAL_TO: greaterThanEqualTo,
  AND: AND,
  OR: OR,
  MAX_LENGTH: maxLength,
  MIN_LENGTH: minLength,
  CONTAINS: contains,
  NOT_CONTAINS: notContains,
  PART_OF: isPartOf,
  NOT_PART_OF: isNotPartOf,
  ALPHA_NUMERIC: isAlphanumeric,
  ALPHANUMERIC: isAlphanumeric,
  ALPHABETS: isAlphabet,
  NUMERIC: isNumeric,
  NO_SPECIAL_CHARACTER: noSpecialCharacters,
  EMAIL: isEmail,
  URL: isUrl,
  YES: isYes,
  NO: isNo,
  IN_THE_FUTURE: isInTheFuture,
  NOT_IN_THE_FUTURE: isNotInTheFuture,
  IN_THE_PAST: isInThePast,
  NOT_IN_THE_PAST: isNotInThePast,
  MAX_NO_OF_SELECTION: maxSelection,
  MIN_NO_OF_SELECTION: minSelection,
  SELECTION_SHOULD_CONTAIN: selectionShouldContain,
  SELECTION_SHOULD_NOT_CONTAIN: selectionShouldNotContain,
  ATTACHMENT_SIZE_IN_MEGA_BYTE: attachmentShouldNotExceed,
  ATTACHMENT_TYPE: attachmentType,
  MAX_NO_OF_ATTACHMENTS: maxNumberOfAttachments,
  MIN_NO_OF_ATTACHMENTS: minNumberOfAttachments,
  IMAGE_HEIGHT_IN_PIXEL: () => {},
  IMAGE_WIDTH_IN_PIXEL: () => {},
  VALUE_EQUAL_TO: equalTo,
  VALUE_NOT_EQUAL_TO: notEqualTo,
  VALUE_LESS_THAN: lessThan,
  VALUE_LESS_THAN_OR_EQUAL_TO: lessThanEqualTo,
  VALUE_GREATER_THAN: greaterThan,
  VALUE_GREATER_THAN_OR_EQUAL_TO: greaterThanEqualTo,
  COUNT_EQUAL_TO: countEqualTo,
  COUNT_NOT_EQUAL_TO: countNotEqualTo,
  COUNT_LESS_THAN: countLessThan,
  COUNT_LESS_THAN_OR_EQUAL_TO: countLessThanEqualTo,
  COUNT_GREATER_THAN: countGreaterThan,
  COUNT_GREATER_THAN_OR_EQUAL_TO: countGreaterThanEqualTo,
  TYPE_EQUAL_TO: () => {},
  TYPE_NOT_EQUAL_TO: () => {},
  SELECT_ALL: selectAll,
  EMPTY: isEmpty,
  NOT_EMPTY: isNotEmpty,
  DOMAIN_EQUAL_TO: domainEqualTo,
  DOMAIN_NOT_EQUAL_TO: domainNotEqualTo,
  STARTS_WITH: startsWith,
  ENDS_WITH: endsWith,
  REGEX: customRegex
};
